/**
 * =============================================================================
 * SPEQ55 RFP Pro - Admin Redirect Component
 * =============================================================================
 *
 * Intelligently redirects admin users to the appropriate admin page based on
 * their role when they navigate to /app/admin without a specific subpage.
 *
 * Redirect Logic:
 * - super_admin, admin_sales → /app/admin/users (User Management)
 * - org_admin → /app/admin/usage (Usage Management - read-only access)
 * - Other roles → /app (main dashboard)
 *
 * This prevents org_admin users from landing on pages they don't have
 * permission to access.
 *
 * @module components/admin/AdminRedirect
 * @author SPEQ55
 */

import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Admin redirect component.
 * Redirects users to the appropriate admin page based on their role.
 *
 * @returns {JSX.Element} Navigate component with role-based redirect
 */
function AdminRedirect() {
  const { user } = useAuth();

  // Redirect super_admin and admin_sales to User Management
  if (user?.role === 'super_admin' || user?.role === 'admin_sales') {
    return <Navigate to="/app/admin/users" replace />;
  }

  // Redirect org_admin to Usage Management (they have read-only access)
  if (user?.role === 'org_admin') {
    return <Navigate to="/app/admin/usage" replace />;
  }

  // Fallback: redirect to main app dashboard
  return <Navigate to="/app" replace />;
}

export default AdminRedirect;
